// Shared Salespads type definitions
// Used by both the Vite dashboard editor and the Next.js public app

// ── Utility Types ──────────────────────────────────────────────

export type TailwindSpacing = 'none' | 'sm' | 'md' | 'lg' | 'xl';
// Maps to: 0 | 2 | 4 | 8 | 16 (in Tailwind spacing units)

export type TailwindFontSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';

export type HeadlineFontSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

/**
 * Block max-width as a percentage (50–100). Undefined = 100%.
 *
 * IMPORTANT: unlike every other `*Mobile` override on the text blocks
 * (headline / subheadline / text), `maxWidthMobile` does **not** fall back to
 * the desktop `maxWidth`. Undefined means 100%, not "inherit desktop". Mobile
 * width is independent by design — a desktop-only narrowing is almost never
 * wanted on a phone. Don't "fix" this back to `maxWidthMobile ?? maxWidth`.
 */
export type BlockWidth = number;

// ── Card Style ────────────────────────────────────────────────

export type BorderRadius = 'none' | 'sm' | 'md' | 'lg';    // 0 | 8px | 12px | 18px
export type BorderWidth = 'thin' | 'medium' | 'thick';      // 1px | 2px | 3px
export type BorderSides = 'all' | 'top-bottom' | 'none';

export interface CardStyle {
  backgroundColor?: string;          // default '#FFFFFF'
  borderEnabled?: boolean;           // default true
  borderColor?: string;              // default '#E4E4E4'
  borderWidth?: BorderWidth;         // default 'thin'
  borderSides?: BorderSides;         // default 'all'
  borderRadius?: BorderRadius;       // default 'lg'
  paddingTop?: number;               // default 30 (px)
  paddingBottom?: number;            // default 50
  paddingLeft?: number;              // default 50
  paddingRight?: number;             // default 50
  paddingTopMobile?: number;         // default 20
  paddingBottomMobile?: number;      // default 30
  paddingLeftMobile?: number;        // default 16
  paddingRightMobile?: number;       // default 16
  borderRadiusMobile?: BorderRadius; // default 'md'
}

// ── Page Data ──────────────────────────────────────────────────

export type LayoutSplit = '50-50' | '60-40' | '40-60' | '33-67' | '67-33';

export interface SalespadPageData {
  version: 1;
  settings: PageSettings;
  blocks: Block[];
  sidebarBlocks?: Block[];     // Blocks for sidebar column (used when layout is 'main-sidebar')
  sidebarTopBlocks?: Block[];  // Bleed-to-edge zone at top of sidebar (breaks out of card padding)
}

export interface PageSettings {
  maxWidth: 'narrow' | 'medium' | 'wide'; // 720px / 960px (wide kept for backwards compat)
  fontFamily: string;
  fontFamilyHeadline?: string; // Falls back to fontFamily if not set
  colorPrimary: string;
  colorBackground: string;
  isBackgroundGradient?: boolean;
  colorText: string;
  animationsEnabled: boolean;
  customHeadScripts: string;
  customBodyScripts: string;
  metaTitle: string;
  metaDescription: string;
  metaImage: string;
  favicon: string;
  containerStyle?: 'flat' | 'card' | 'full-width';
  contentBackgroundColor?: string;              // Full-width content section bg (default '#FFFFFF')
  contentBorderColor?: string;                  // Full-width content top border color (default '#E4E4E4')
  contentBorderWidth?: number;                  // Full-width content top border width in px (default 0 = none)
  cardStyle?: CardStyle;                        // Main card (or single card) styling
  sidebarCardStyle?: CardStyle;                 // Sidebar card styling (only when layout is 'main-sidebar')
  layout?: 'single' | 'main-sidebar';          // default 'single'
  layoutSplit?: LayoutSplit;                    // default '60-40'
  sidebarMobilePosition?: 'below' | 'above';   // default 'below'
  sidebarHeaderEnabled?: boolean;               // default false — show header zone above sidebar blocks
  header?: {
    logoUrl: string;
    logoAlign?: 'left' | 'center'; // desktop alignment, default 'left'
    logoAlignMobile?: 'left' | 'center'; // mobile alignment, default 'left'
    logoSize?: 'sm' | 'md' | 'lg'; // desktop size, default 'md'
    logoSizeMobile?: 'sm' | 'md' | 'lg'; // mobile size, default same as desktop
  };
  footer?: {
    disclaimerText?: string;
    copyrightText?: string;
    links?: Array<{ label: string; url: string }>;
    textColor?: string; // default '#9BA2AE'
  };
}

// ── Block Types ────────────────────────────────────────────────

export type BlockType =
  | 'hero'
  | 'headline'
  | 'subheadline'
  | 'text'
  | 'bullet-list'
  | 'button'
  | 'button-modal'
  | 'video'
  | 'video-popup'
  | 'booking'
  | 'testimonials'
  | 'faq'
  | 'two-column'
  | 'avatar-stack'
  | 'image';

export interface BaseBlock {
  id: string;
  type: BlockType;
  visible: boolean;
  paddingTop: TailwindSpacing;
  paddingBottom: TailwindSpacing;
  paddingTopMobile?: TailwindSpacing;
  paddingBottomMobile?: TailwindSpacing;
}

// ── Individual Block Definitions ───────────────────────────────

export interface HeroBlock extends BaseBlock {
  type: 'hero';
  data: {
    headline: string;
    subheadline?: string;
    backgroundType: 'color' | 'image' | 'gradient';
    backgroundColor?: string;
    backgroundImage?: string;
    backgroundGradient?: { from: string; to: string; direction: string };
    textColor: string;
    textAlign: 'left' | 'center' | 'right';
    headlineFontSize: TailwindFontSize;
    subheadlineFontSize: TailwindFontSize;
    minHeight: 'small' | 'medium' | 'large' | 'full'; // 300px / 450px / 600px / 100vh
    ctaButton?: {
      text: string;
      url?: string;
      scrollToBlock?: string;
      style: 'primary' | 'secondary' | 'outline';
    };
  };
}

export interface HeadlineBlock extends BaseBlock {
  type: 'headline';
  data: {
    text: string;
    level?: 'h1' | 'h2' | 'h3';
    fontSize: HeadlineFontSize;
    fontSizeMobile?: HeadlineFontSize;
    textAlign: 'left' | 'center' | 'right';
    textAlignMobile?: 'left' | 'center' | 'right';
    color?: string;
    colorMobile?: string;
    fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
    fontWeightMobile?: 'normal' | 'medium' | 'semibold' | 'bold';
    letterSpacing?: string; // e.g. '-0.03em' or '-1.26px'
    letterSpacingMobile?: string;
    lineHeight?: number; // multiplier e.g. 1.2, undefined = default
    lineHeightMobile?: number;
    maxWidth?: BlockWidth;
    maxWidthMobile?: BlockWidth;
  };
}

export interface TextBlock extends BaseBlock {
  type: 'text';
  data: {
    content: string;
    fontSize: TailwindFontSize;
    fontSizeMobile?: TailwindFontSize;
    textAlign: 'left' | 'center' | 'right';
    textAlignMobile?: 'left' | 'center' | 'right';
    color?: string;
    colorMobile?: string;
    fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
    fontWeightMobile?: 'normal' | 'medium' | 'semibold' | 'bold';
    letterSpacing?: string; // e.g. '-0.05em' or '-1.26px'
    letterSpacingMobile?: string;
    lineHeight?: number;
    lineHeightMobile?: number;
    maxWidth?: BlockWidth;
    maxWidthMobile?: BlockWidth;
  };
}

export interface SubheadlineBlock extends BaseBlock {
  type: 'subheadline';
  data: {
    content: string;
    fontSize: TailwindFontSize;
    fontSizeMobile?: TailwindFontSize;
    textAlign: 'left' | 'center' | 'right';
    textAlignMobile?: 'left' | 'center' | 'right';
    color?: string;
    colorMobile?: string;
    fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
    fontWeightMobile?: 'normal' | 'medium' | 'semibold' | 'bold';
    letterSpacing?: string;
    letterSpacingMobile?: string;
    lineHeight?: number;
    lineHeightMobile?: number;
    maxWidth?: BlockWidth;
    maxWidthMobile?: BlockWidth;
  };
}

export interface BulletListBlock extends BaseBlock {
  type: 'bullet-list';
  data: {
    items: string[];
    icon: string;
    iconColor?: string;
    fontSize: TailwindFontSize;
    fontSizeMobile?: TailwindFontSize;
  };
}

export interface ButtonBlock extends BaseBlock {
  type: 'button';
  data: {
    text: string;
    url: string;
    clickAction?: 'link' | 'salespad' | 'modal'; // default 'link' for backwards compat
    eventId?: string;      // when clickAction is 'modal'
    salespadId?: string;   // when clickAction is 'salespad'
    style: 'primary' | 'secondary' | 'outline';
    size: 'sm' | 'md' | 'lg';
    sizeMobile?: 'sm' | 'md' | 'lg';
    align: 'left' | 'center' | 'right';
    alignMobile?: 'left' | 'center' | 'right';
    color?: string;
    textColor?: string;
    isGradient?: boolean;
    fullWidth: boolean;
  };
}

export interface ButtonModalBlock extends BaseBlock {
  type: 'button-modal';
  data: {
    text: string;
    style: 'primary' | 'secondary' | 'outline';
    size: 'sm' | 'md' | 'lg';
    align: 'left' | 'center' | 'right';
    color?: string;
    textColor?: string;
    isGradient?: boolean;
    fullWidth: boolean;
    modalContent: 'booking';
    eventId: string;
  };
}

export interface VideoBlock extends BaseBlock {
  type: 'video';
  data: {
    url: string;
    aspectRatio: '16:9' | '4:3' | '1:1' | '9:16';
    aspectRatioMobile?: '16:9' | '4:3' | '1:1' | '9:16';
    maxWidth: 'small' | 'medium' | 'large' | 'full';
    maxWidthMobile?: 'small' | 'medium' | 'large' | 'full';
    autoplay?: boolean;
    muted?: boolean;
    loop?: boolean;
    controls?: boolean; // default true
    borderRadius?: 'none' | 'sm' | 'md' | 'lg' | 'xl'; // default 'lg' (12px)
    useCustomHtml?: boolean;
    customHtml?: string;
    /** Poster/thumbnail image rendered server-side until the user clicks play.
     * Used by the YouTube/Wistia facade pattern to keep the video player's
     * runtime out of initial page load. Optional — falls back to a play button
     * on a dark background when missing. */
    posterImageUrl?: string;
    banner?: {
      enabled: boolean;
      enabledMobile?: boolean;
      text: string;
      backgroundColor: string;
      backgroundColorMobile?: string;
      isGradient?: boolean;
      icon?: string;
      fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
      fontWeightMobile?: 'normal' | 'medium' | 'semibold' | 'bold';
      fontSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'; // default 'lg'
      fontSizeMobile?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    };
  };
}

export interface VideoPopupBlock extends BaseBlock {
  type: 'video-popup';
  data: {
    /** Source video URL (YouTube, Vimeo, Wistia, Loom). */
    url: string;
    /** Poster auto-resolved from the provider via the resolve-video edge
     * function at edit time. Rendered server-side; no runtime API call. */
    thumbnailUrl?: string;
    /** Uploaded poster override. Wins over thumbnailUrl when set — used when
     * auto-resolution fails (private video) or the user wants a custom frame. */
    customThumbnailUrl?: string;
    /** Aspect ratio of the on-page thumbnail. The modal player uses the
     * video's own ratio. Auto-fetched posters are object-fit: cover'd. */
    thumbnailAspectRatio: '16:9' | '4:3' | '1:1' | '9:16';
    thumbnailAspectRatioMobile?: '16:9' | '4:3' | '1:1' | '9:16';
    maxWidth: 'small' | 'medium' | 'large' | 'full';
    maxWidthMobile?: 'small' | 'medium' | 'large' | 'full';
    borderRadius?: 'none' | 'sm' | 'md' | 'lg' | 'xl'; // default 'lg'
    align?: 'left' | 'center' | 'right'; // thumbnail alignment, default 'center'
    playIconColor?: string;   // play triangle colour, default '#FFFFFF'
    /** Fill colour for the play bar — shown semi-transparent behind the play
     * icon at rest and expanded across the whole thumbnail on hover. Solid hex
     * or rgba; transparency is applied at render via color-mix. */
    playFillColor?: string;   // default '#000000'
    /** Dark gradient scrim over the thumbnail for play-button contrast. */
    overlayScrim?: boolean;   // default true
    /** Warm the player connection on hover/focus. */
    preloadOnHover?: boolean; // default true
  };
}

export interface BookingBlock extends BaseBlock {
  type: 'booking';
  data: {
    eventId: string;
    /** 'inline' renders the form on the page; 'modal' (SmartFlow only) renders a
     * button that opens the form in a popup. */
    displayMode: 'inline' | 'modal';
    /** Button appearance for displayMode === 'modal'. Salespad-standard styling
     * (mirrors ButtonModalBlock's button options). */
    button?: {
      text: string;
      style: 'primary' | 'secondary' | 'outline';
      size: 'sm' | 'md' | 'lg';
      align: 'left' | 'center' | 'right';
      color?: string;
      textColor?: string;
      isGradient?: boolean;
      fullWidth: boolean;
    };
  };
}

/** Seed values applied when a booking block is first switched to modal display
 * mode, and the render-time fallback when `button` is absent. */
export const DEFAULT_BOOKING_MODAL_BUTTON: NonNullable<BookingBlock['data']['button']> = {
  text: 'Book a call',
  style: 'primary',
  size: 'md',
  align: 'center',
  fullWidth: false,
};

export interface TestimonialsBlock extends BaseBlock {
  type: 'testimonials';
  data: {
    layout: 'cards' | 'single' | 'carousel';
    items: Array<{
      id: string;
      quote: string;
      name: string;
      title?: string;
      avatarUrl?: string;
      rating?: number;
    }>;
    backgroundColor?: string;
  };
}

export interface FAQBlock extends BaseBlock {
  type: 'faq';
  data: {
    headline?: string;
    items: Array<{
      id: string;
      question: string;
      answer: string;
    }>;
  };
}

export interface AvatarStackBlock extends BaseBlock {
  type: 'avatar-stack';
  data: {
    avatars: string[]; // image URLs
    text: string;
    textColor?: string;
    showStars: boolean;
    starRating: number; // e.g. 5.0
    starColor?: string;
    align: 'left' | 'center' | 'right';
    avatarSize: 'sm' | 'md' | 'lg';
  };
}

export interface ImageBlock extends BaseBlock {
  type: 'image';
  data: {
    url: string;
    alt?: string;
    naturalWidth?: number;
    naturalHeight?: number;
    maxWidth: 'small' | 'medium' | 'large' | 'full' | 'custom';
    maxWidthMobile?: 'small' | 'medium' | 'large' | 'full' | 'custom';
    /** Pixel width when maxWidth is 'custom'. Falls back to 800 if missing. */
    customWidth?: number;
    /** Pixel width when maxWidthMobile is 'custom'. Falls back to customWidth, then 800. */
    customWidthMobile?: number;
    borderRadius?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
    align: 'left' | 'center' | 'right';
    linkUrl?: string;
    linkTarget?: '_self' | '_blank';
  };
}

export interface TwoColumnBlock extends BaseBlock {
  type: 'two-column';
  data: {
    split: LayoutSplit;
    stackOnMobile: boolean;
    leftBlocks: Block[];
    rightBlocks: Block[];
    /** Vertical alignment of left column contents within the column. Default 'top'. */
    leftVerticalAlign?: 'top' | 'center' | 'bottom';
    /** Vertical alignment of right column contents within the column. Default 'top'. */
    rightVerticalAlign?: 'top' | 'center' | 'bottom';
  };
}

// ── Union Type ─────────────────────────────────────────────────

export type Block =
  | HeroBlock
  | HeadlineBlock
  | SubheadlineBlock
  | TextBlock
  | BulletListBlock
  | ButtonBlock
  | ButtonModalBlock
  | VideoBlock
  | VideoPopupBlock
  | BookingBlock
  | TestimonialsBlock
  | FAQBlock
  | TwoColumnBlock
  | AvatarStackBlock
  | ImageBlock;

// ── Database Row Types ─────────────────────────────────────────

export type SalespadStatus = 'draft' | 'published' | 'archived';

export interface Salespad {
  id: string;
  team_id: string;
  user_id: string;
  title: string;
  slug: string;
  status: SalespadStatus;
  template_id: string | null;
  page_data: SalespadPageData;
  event_id: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SalespadAnalyticsEvent {
  id: string;
  salespad_id: string;
  event_type: 'page_view' | 'unique_visit' | 'booking_started' | 'booking_completed';
  visitor_id: string | null;
  referrer: string | null;
  user_agent: string | null;
  country: string | null;
  created_at: string;
}

export interface SalespadAsset {
  id: string;
  user_id: string;
  salespad_id: string | null;
  file_path: string;
  file_name: string;
  file_size: number | null;
  mime_type: string | null;
  created_at: string;
}

export interface SalespadTemplate {
  id: string;
  name: string;
  description: string | null;
  preview_image: string | null;
  page_data: SalespadPageData;
  sort_order: number;
  is_active: boolean;
  created_at: string;
}

// ── Logo Size ────────────────────────────────────────────────

export const logoSizePx: Record<'sm' | 'md' | 'lg', number> = {
  sm: 25, md: 50, lg: 75,
};

// ── Card Style Defaults & Helpers ──────────────────────────────

export const borderRadiusPx: Record<BorderRadius, number> = {
  none: 0, sm: 8, md: 12, lg: 18,
};

export const borderWidthPx: Record<BorderWidth, number> = {
  thin: 1, medium: 2, thick: 3,
};

export const DEFAULT_CARD_STYLE: Required<CardStyle> = {
  backgroundColor: '#FFFFFF',
  borderEnabled: true,
  borderColor: '#E4E4E4',
  borderWidth: 'thin',
  borderSides: 'all',
  borderRadius: 'lg',
  paddingTop: 30,
  paddingBottom: 50,
  paddingLeft: 50,
  paddingRight: 50,
  paddingTopMobile: 20,
  paddingBottomMobile: 30,
  paddingLeftMobile: 16,
  paddingRightMobile: 16,
  borderRadiusMobile: 'md',
};

export function resolveCardStyle(style?: CardStyle): Required<CardStyle> {
  return { ...DEFAULT_CARD_STYLE, ...style };
}

// ── Default Values ─────────────────────────────────────────────

export const DEFAULT_PAGE_SETTINGS: PageSettings = {
  maxWidth: 'medium',
  fontFamily: 'Inter',
  colorPrimary: '#2563eb',
  colorBackground: '#ffffff',
  colorText: '#1f2937',
  animationsEnabled: false,
  customHeadScripts: '',
  customBodyScripts: '',
  metaTitle: '',
  metaDescription: '',
  metaImage: '',
  favicon: '',
};

export const DEFAULT_PAGE_DATA: SalespadPageData = {
  version: 1,
  settings: DEFAULT_PAGE_SETTINGS,
  blocks: [],
  sidebarBlocks: [],
  sidebarTopBlocks: [],
};
